import React, { useState, useEffect } from 'react';
import { saveField, loadUserData } from '../supabase';

const TABS = ['Daily', 'Weekly', 'Monthly', 'Journal', 'History'];

function getWeekKey() {
  const d = new Date();
  const start = new Date(d);
  start.setDate(d.getDate() - d.getDay());
  return `week_${start.toISOString().slice(0, 10)}`;
}
function getDayKey() { return `day_${new Date().toISOString().slice(0, 10)}`; }
function getMonthKey() {
  const d = new Date();
  return `month_${d.getFullYear()}_${String(d.getMonth() + 1).padStart(2, '0')}`;
}

async function getJournal(userId) {
  if (userId) {
    const ud = await loadUserData(userId);
    return ud?.journal || {};
  }
  const j = {};
  for (let i = 0; i < localStorage.length; i++) {
    const k = localStorage.key(i);
    if (k && k.startsWith('bhn_journal_')) j[k.replace('bhn_journal_','')] = JSON.parse(localStorage.getItem(k));
  }
  return j;
}

async function setJournalKey(userId, key, val) {
  if (userId) {
    const ud = await loadUserData(userId);
    const journal = { ...(ud?.journal || {}), [key]: val };
    await saveField(userId, 'journal', journal);
  } else {
    localStorage.setItem(`bhn_journal_${key}`, JSON.stringify(val));
  }
}

function DailyTab({ userId }) {
  const key = getDayKey();
  // New daily shape:
  //   intention          — morning intention (carried over from old schema)
  //   presentToday       — Evening Pass Q1
  //   fellShort          — Evening Pass Q2
  //   taught             — Evening Pass Q3
  //   oneThing           — Tomorrow's Shape Q1
  //   moment             — Tomorrow's Shape Q2
  //   feelTomorrow       — Tomorrow's Shape Q3
  // Legacy fields preserved on read so existing saved entries don't lose content:
  //   wentWell           — old single evening prompt
  //   tomorrow           — old array of up to 6 tasks
  const EMPTY = {
    intention: '',
    presentToday: '', fellShort: '', taught: '',
    oneThing: '', moment: '', feelTomorrow: '',
  };
  const [data, setData] = useState(EMPTY);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    (async () => {
      const j = await getJournal(userId);
      if (j[key]) {
        // Merge stored data onto EMPTY so missing new fields default to '',
        // but legacy fields (wentWell, tomorrow) are still preserved on the object.
        setData({ ...EMPTY, ...j[key] });
      }
      setLoaded(true);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, key]);

  async function update(field, val) {
    const updated = { ...data, [field]: val };
    setData(updated);
    await setJournalKey(userId, key, updated);
  }

  async function refresh() {
    setData(EMPTY);
    await setJournalKey(userId, key, EMPTY);
  }

  if (!loaded) return <div style={{ padding: '20px', textAlign: 'center', color: '#C8C4BE', fontSize: 12 }}>Loading...</div>;

  // Legacy content from pre-upgrade entries — show a gentle banner so users can
  // carry anything important forward into the new prompts.
  const hasLegacy = (data.wentWell && data.wentWell.trim()) ||
    (Array.isArray(data.tomorrow) && data.tomorrow.some(t => t && t.trim()));

  return (
    <div style={{ width: '100%' }}>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
        <button onClick={refresh} style={{ fontSize: 11, color: '#A09888', background: 'none', border: '1px solid #E0DDD8', borderRadius: 7, padding: '5px 12px', cursor: 'pointer', fontWeight: 500 }}>↺ Refresh</button>
      </div>

      {hasLegacy && (
        <div style={{ background: '#FAFAF8', border: '1px solid #E8E4DF', borderRadius: 10, padding: '12px 14px', marginBottom: 18 }}>
          <div style={{ fontSize: 10, letterSpacing: '.1em', textTransform: 'uppercase', color: '#A09888', fontWeight: 600, marginBottom: 6 }}>From your earlier entry today</div>
          {data.wentWell && data.wentWell.trim() && (
            <div style={{ fontSize: 12, color: '#555', lineHeight: 1.6, marginBottom: 6 }}>
              <strong style={{ color: '#777', fontWeight: 600 }}>What went well:</strong> {data.wentWell}
            </div>
          )}
          {Array.isArray(data.tomorrow) && data.tomorrow.filter(t => t && t.trim()).length > 0 && (
            <div style={{ fontSize: 12, color: '#555', lineHeight: 1.6 }}>
              <strong style={{ color: '#777', fontWeight: 600 }}>Tomorrow's tasks:</strong> {data.tomorrow.filter(t => t && t.trim()).join(' · ')}
            </div>
          )}
        </div>
      )}

      <div className="j-section">
        <div className="j-title">Morning intention</div>
        <div className="j-prompt">What do you want to be present for today?</div>
        <textarea className="j-textarea" placeholder="Write your intention for today..." value={data.intention} onChange={e => update('intention', e.target.value)} rows={3} style={{ width: '100%' }} />
      </div>

      <div className="j-section">
        <div className="j-title">Evening pass</div>
        <div className="j-prompt">Three questions as the day closes.</div>

        <div className="input-label" style={{ marginTop: 14 }}>Where was I most present today?</div>
        <textarea className="j-textarea" placeholder="A moment you were really here..." value={data.presentToday} onChange={e => update('presentToday', e.target.value)} rows={2} style={{ width: '100%' }} />

        <div className="input-label" style={{ marginTop: 14 }}>Where did I fall short of who I want to be?</div>
        <textarea className="j-textarea" placeholder="Honest, not harsh..." value={data.fellShort} onChange={e => update('fellShort', e.target.value)} rows={2} style={{ width: '100%' }} />

        <div className="input-label" style={{ marginTop: 14 }}>What did today teach me?</div>
        <textarea className="j-textarea" placeholder="A small noticing counts..." value={data.taught} onChange={e => update('taught', e.target.value)} rows={2} style={{ width: '100%' }} />
      </div>

      <div className="j-section">
        <div className="j-title">Tomorrow's shape</div>
        <div className="j-prompt">Three beats, not a to-do list.</div>

        <div className="input-label" style={{ marginTop: 14 }}>The one thing that must happen</div>
        <input className="form-input" placeholder="Just one." value={data.oneThing} onChange={e => update('oneThing', e.target.value)} />

        <div className="input-label" style={{ marginTop: 14 }}>The moment I want to be most present for</div>
        <input className="form-input" placeholder="A conversation, a meal, a walk..." value={data.moment} onChange={e => update('moment', e.target.value)} />

        <div className="input-label" style={{ marginTop: 14 }}>How I want to feel when my head hits the pillow tomorrow night</div>
        <input className="form-input" placeholder="One or two words." value={data.feelTomorrow} onChange={e => update('feelTomorrow', e.target.value)} />
      </div>

      <div style={{ fontSize: 11, color: '#C8C4BE', textAlign: 'center', paddingBottom: 8 }}>Saves automatically as you type</div>
    </div>
  );
}

function WeeklyTab({ userId }) {
  const key = getWeekKey();
  const [data, setData] = useState({ proud: '', learning: '', barriers: '', improve: '' });
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    (async () => {
      const j = await getJournal(userId);
      if (j[key]) setData(j[key]);
      setLoaded(true);
    })();
  }, [userId, key]);

  async function update(field, val) {
    const updated = { ...data, [field]: val };
    setData(updated);
    await setJournalKey(userId, key, updated);
  }

  async function refresh() {
    const empty = { proud: '', learning: '', barriers: '', improve: '' };
    setData(empty);
    await setJournalKey(userId, key, empty);
  }

  const weekStart = new Date();
  weekStart.setDate(weekStart.getDate() - weekStart.getDay());
  const weekEnd = new Date(weekStart);
  weekEnd.setDate(weekStart.getDate() + 6);

  if (!loaded) return <div style={{ padding: '20px', textAlign: 'center', color: '#C8C4BE', fontSize: 12 }}>Loading...</div>;

  return (
    <div style={{ width: '100%' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div style={{ fontSize: 12, color: '#A09888', fontWeight: 500 }}>
          {weekStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} – {weekEnd.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
        </div>
        <button onClick={refresh} style={{ fontSize: 11, color: '#A09888', background: 'none', border: '1px solid #E0DDD8', borderRadius: 7, padding: '5px 12px', cursor: 'pointer', fontWeight: 500 }}>↺ Refresh</button>
      </div>
      {[
        { key: 'proud', label: 'What am I proud of this week?', placeholder: 'Celebrate your wins...' },
        { key: 'learning', label: 'What am I learning?', placeholder: 'About yourself and your patterns...' },
        { key: 'barriers', label: 'What barriers got in the way?', placeholder: 'What made this week challenging?...' },
        { key: 'improve', label: 'How can I improve next week?', placeholder: 'One or two honest intentions...' },
      ].map(({ key: field, label, placeholder }) => (
        <div key={field} className="j-section">
          <div className="j-title">{label}</div>
          <textarea className="j-textarea" placeholder={placeholder} value={data[field]} onChange={e => update(field, e.target.value)} rows={4} style={{ width: '100%' }} />
        </div>
      ))}
      <div style={{ fontSize: 11, color: '#C8C4BE', textAlign: 'center', paddingBottom: 8 }}>Saves automatically · Resets each new week</div>
    </div>
  );
}

function MonthlyTab({ userId }) {
  const key = getMonthKey();
  // Monthly shape:
  //   wins — array of 3 slots, each { title, learned }
  //   checkIn — single object: { rating_of, score, make_ten }
  const EMPTY = {
    wins: [{ title: '', learned: '' }, { title: '', learned: '' }, { title: '', learned: '' }],
    checkIn: { rating_of: '', score: '', make_ten: '' },
  };
  const [data, setData] = useState(EMPTY);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    (async () => {
      const j = await getJournal(userId);
      if (j[key]) {
        // Defensive merge — if saved data is partial, fill gaps with empty defaults.
        const saved = j[key];
        setData({
          wins: Array.isArray(saved.wins) && saved.wins.length === 3 ? saved.wins : EMPTY.wins,
          checkIn: { ...EMPTY.checkIn, ...(saved.checkIn || {}) },
        });
      }
      setLoaded(true);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, key]);

  async function updateWin(idx, field, val) {
    const wins = data.wins.map((w, i) => i === idx ? { ...w, [field]: val } : w);
    const updated = { ...data, wins };
    setData(updated);
    await setJournalKey(userId, key, updated);
  }

  async function updateCheckIn(field, val) {
    const checkIn = { ...data.checkIn, [field]: val };
    const updated = { ...data, checkIn };
    setData(updated);
    await setJournalKey(userId, key, updated);
  }

  async function refresh() {
    setData(EMPTY);
    await setJournalKey(userId, key, EMPTY);
  }

  const monthName = new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

  if (!loaded) return <div style={{ padding: '20px', textAlign: 'center', color: '#C8C4BE', fontSize: 12 }}>Loading...</div>;

  return (
    <div style={{ width: '100%' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div style={{ fontSize: 12, color: '#A09888', fontWeight: 500 }}>{monthName}</div>
        <button onClick={refresh} style={{ fontSize: 11, color: '#A09888', background: 'none', border: '1px solid #E0DDD8', borderRadius: 7, padding: '5px 12px', cursor: 'pointer', fontWeight: 500 }}>↺ Refresh</button>
      </div>

      <div className="j-section">
        <div className="j-title">Your track record</div>
        <div className="j-prompt">Three wins from this month — big or quiet. What did each teach you?</div>

        {data.wins.map((win, i) => (
          <div key={i} style={{ marginTop: 16, paddingTop: 14, borderTop: i === 0 ? 'none' : '1px solid #F5F3F0' }}>
            <div className="input-label">Win {i + 1}</div>
            <input className="form-input" placeholder="What happened?" value={win.title} onChange={e => updateWin(i, 'title', e.target.value)} />
            <div className="input-label">What it taught you</div>
            <textarea className="j-textarea" placeholder="One sentence is enough..." value={win.learned} onChange={e => updateWin(i, 'learned', e.target.value)} rows={2} style={{ width: '100%' }} />
          </div>
        ))}
      </div>

      <div className="j-section">
        <div className="j-title">The 1 to 10 check-in</div>
        <div className="j-prompt">Pick one area of your life and rate it honestly.</div>

        <div className="input-label" style={{ marginTop: 14 }}>What are you rating?</div>
        <input className="form-input" placeholder="A relationship, a project, your work, your health..." value={data.checkIn.rating_of} onChange={e => updateCheckIn('rating_of', e.target.value)} />

        <div className="input-label" style={{ marginTop: 14 }}>Rating (1–10)</div>
        <input className="form-input" type="number" min="1" max="10" placeholder="1–10" value={data.checkIn.score} onChange={e => updateCheckIn('score', e.target.value)} />

        <div className="input-label" style={{ marginTop: 14 }}>What would make it a 10?</div>
        <textarea className="j-textarea" placeholder="What's missing? What would have to change?" value={data.checkIn.make_ten} onChange={e => updateCheckIn('make_ten', e.target.value)} rows={3} style={{ width: '100%' }} />
      </div>

      <div style={{ fontSize: 11, color: '#C8C4BE', textAlign: 'center', paddingBottom: 8 }}>Saves automatically · Resets each new month</div>
    </div>
  );
}

function JournalTab({ userId }) {
  const [draft, setDraft] = useState('');
  const [saved, setSaved] = useState(false);

  async function saveToHistory() {
    const entryKey = `freewrite_${Date.now()}`;
    const entry = { text: draft, date: new Date().toISOString(), id: entryKey };
    const j = await getJournal(userId);
    const history = j['freewrite_history'] || [];
    await setJournalKey(userId, 'freewrite_history', [...history, entry]);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  }

  async function refresh() {
    setDraft('');
  }

  return (
    <div style={{ width: '100%' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <div style={{ fontSize: 13, color: '#A09888', lineHeight: 1.7 }}>Open space. Write freely — this is yours.</div>
        <button onClick={refresh} style={{ fontSize: 11, color: '#A09888', background: 'none', border: '1px solid #E0DDD8', borderRadius: 7, padding: '5px 12px', cursor: 'pointer', fontWeight: 500, flexShrink: 0, marginLeft: 8 }}>↺ Refresh</button>
      </div>
      <textarea className="j-textarea" placeholder="Begin writing..." value={draft} onChange={e => setDraft(e.target.value)} rows={12} style={{ minHeight: 240, width: '100%' }} />
      <button onClick={saveToHistory} style={{ marginTop: 16, padding: '10px 20px', background: saved ? '#3A6A2A' : 'white', border: '1px solid', borderColor: saved ? '#3A6A2A' : '#E0DDD8', borderRadius: 8, fontSize: 12, fontWeight: 600, color: saved ? 'white' : '#777', cursor: 'pointer', transition: 'all .15s' }}>
        {saved ? '✓ Saved to history' : 'Save to history'}
      </button>
    </div>
  );
}

function HistoryTab({ userId }) {
  const [entries, setEntries] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState('');
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    async function loadEntries() {
      const j = await getJournal(userId);
      const history = j['freewrite_history'] || [];
      setEntries([...history].reverse());
      setLoaded(true);
    }
    loadEntries();
  }, [userId]);

  async function deleteEntry(id) {
    const j = await getJournal(userId);
    const history = (j['freewrite_history'] || []).filter(e => e.id !== id);
    await setJournalKey(userId, 'freewrite_history', history);
    setEntries(entries.filter(e => e.id !== id));
  }

  async function saveEdit(id) {
    const j = await getJournal(userId);
    const history = (j['freewrite_history'] || []).map(e => e.id === id ? { ...e, text: editText } : e);
    await setJournalKey(userId, 'freewrite_history', history);
    setEntries(entries.map(e => e.id === id ? { ...e, text: editText } : e));
    setEditingId(null);
  }

  if (!loaded) return <div style={{ textAlign: 'center', padding: '32px 0', fontSize: 13, color: '#A09888' }}>Loading...</div>;

  if (entries.length === 0) return (
    <div style={{ textAlign: 'center', padding: '32px 0' }}>
      <div style={{ fontSize: 14, fontWeight: 600, color: '#111', marginBottom: 6 }}>No journal entries yet</div>
      <div style={{ fontSize: 12, color: '#A09888', lineHeight: 1.7 }}>Write in the Journal tab and save to history.</div>
    </div>
  );

  return (
    <div style={{ width: '100%' }}>
      {entries.map(entry => (
        <div key={entry.id} style={{ padding: '16px 0', borderBottom: '1px solid #F5F3F0' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <div style={{ fontSize: 11, color: '#A09888', fontWeight: 500 }}>{new Date(entry.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={() => { setEditingId(entry.id); setEditText(entry.text); }} style={{ fontSize: 11, color: '#3A6A2A', background: 'none', border: '1px solid #3A6A2A', borderRadius: 6, padding: '4px 10px', cursor: 'pointer', fontWeight: 500 }}>Edit</button>
              <button onClick={() => deleteEntry(entry.id)} style={{ fontSize: 11, color: '#8B2020', background: 'none', border: '1px solid #E8D0D0', borderRadius: 6, padding: '4px 10px', cursor: 'pointer', fontWeight: 500 }}>Delete</button>
            </div>
          </div>
          {editingId === entry.id ? (
            <div>
              <textarea value={editText} onChange={e => setEditText(e.target.value)} className="j-textarea" rows={6} style={{ width: '100%', marginBottom: 10 }} />
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={() => saveEdit(entry.id)} className="btn-primary" style={{ flex: 1, marginBottom: 0, padding: '8px' }}>Save</button>
                <button onClick={() => setEditingId(null)} className="btn-secondary" style={{ flex: 1, marginBottom: 0, padding: '8px' }}>Cancel</button>
              </div>
            </div>
          ) : (
            <div style={{ fontSize: 13, color: '#444', lineHeight: 1.75 }}>{entry.text}</div>
          )}
        </div>
      ))}
    </div>
  );
}

export default function Journal({ userId }) {
  const [tab, setTab] = useState(0);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', height: '100%' }}>
      <div className="top-bar">
        <div className="brand-small">Journal</div>
        <div className="screen-title">Reflection</div>
      </div>
      <div style={{ display: 'flex', background: '#FFFFFF', borderBottom: '1px solid #F0EDE8', flexShrink: 0, overflowX: 'auto' }}>
        {TABS.map((t, i) => (
          <button key={t} onClick={() => setTab(i)} style={{ flex: 1, padding: '12px 8px', background: 'none', border: 'none', fontSize: 11, fontWeight: 600, cursor: 'pointer', letterSpacing: '.04em', textTransform: 'uppercase', color: tab === i ? '#111' : '#C8C4BE', borderBottom: tab === i ? '2px solid #111' : '2px solid transparent', transition: 'all .15s', whiteSpace: 'nowrap', minWidth: 80 }}>{t}</button>
        ))}
      </div>
      <div className="screen-body" style={{ width: '100%' }}>
        {tab === 0 && <DailyTab userId={userId} />}
        {tab === 1 && <WeeklyTab userId={userId} />}
        {tab === 2 && <MonthlyTab userId={userId} />}
        {tab === 3 && <JournalTab userId={userId} />}
        {tab === 4 && <HistoryTab userId={userId} />}
      </div>
    </div>
  );
}
